import Image from 'next/image';
import styled from 'styled-components';
import { AiOutlineMore } from 'react-icons/ai';
import { devices } from '@src/styles/theme';
import { IComment, NestReplies } from '@src/interfaces/community';
import { useQuery } from '@tanstack/react-query';
import { authKeys } from '@src/keys/authKey';
import { auth } from '@src/services/api';
import { useInsertComment, useDeleteComment } from '@src/hooks/useCommunitys';
import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState } from 'recoil';
import { modalAtom } from '@src/recoils/atom/modal';
import { communintyDateFormat } from '@src/utils/date';
import useAuth from '@src/hooks/useAuth';

type Props = {
  replies: Array<IComment>;
  communityId: string;
};

const Comment = ({ replies, communityId }: Props) => {
  const { fetchProfile } = useAuth();
  const { data: profile } = fetchProfile();
  const [commentStr, setCommentStr] = useState('');
  const [replyCommentStr, setReplyCommentStr] = useState('');
  const [selectReplyUuid, setSelectReplyUuid] = useState('');
  const router = useRouter();
  const type = router.pathname.startsWith('/community/board/detail')
    ? 'board'
    : 'donation';
  const { mutate: onComment } = useInsertComment(communityId, type);
  const { mutate: onDeleteComment } = useDeleteComment(communityId, type);
  const [update, setUpdate] = useState('');
  const setModal = useSetRecoilState(modalAtom);
  const onClickReply = useCallback(
    (id: string) => {
      // 답글쓰기를 두번 누르면 닫는다.
      if (selectReplyUuid !== '') {
        setSelectReplyUuid('');
        return;
      }
      // 답변 초기화
      if (replyCommentStr !== '') setReplyCommentStr('');
      setSelectReplyUuid(id);
    },
    [selectReplyUuid, replyCommentStr]
  );

  const onSubmitComment = useCallback(
    (isReply: boolean, id?: string) => {
      if (isReply) {
        const body: any = {
          requestData: {
            content: replyCommentStr,
            donationUuid: communityId,
            uuid: id,
          },
          isReply,
        };
        onComment(body);
        setReplyCommentStr('');
        setSelectReplyUuid('');
      } else {
        const body: any = {
          requestData: {
            content: commentStr,
            donationUuid: communityId,
            uuid: communityId,
          },
          isReply,
        };
        onComment(body);
        setCommentStr('');
      }
    },
    [commentStr, replyCommentStr]
  );

  const onChangeText = useCallback((isReply: boolean, value: string) => {
    if (isReply) {
      setReplyCommentStr(value);
    } else {
      setCommentStr(value);
    }
  }, []);

  const onSubmitDelete = useCallback((isReply: boolean, id?: string) => {
    if (isReply) {
      const body: any = {
        requestData: {
          content: '',
          donationUuid: communityId,
          uuid: id,
        },
        isReply,
      };
      onDeleteComment(body);
      setUpdate('');
    } else {
      const body: any = {
        requestData: {
          content: '',
          donationUuid: communityId,
          uuid: id,
        },
        isReply,
      };
      onDeleteComment(body);
      setUpdate('');
    }
  }, []);

  return (
    <>
      {/* 로그인을 했을때 답변리스트가 없는경우 */}
      {profile?.status === 'ACTIVE' && (
        <CommentContainer>
          <FlexWidth100>
            <Profile src={profile.profileImageUrl} alt={'profile'} />
            <CommentInput
              value={commentStr}
              placeholder={'응원의 한마디...'}
              onChange={(e) => {
                console.log(e.target.value);
                onChangeText(false, e.target.value);
              }}
            />
            <CommentButton
              isActive={commentStr.length >= 1}
              onClick={() => {
                onSubmitComment(false);
              }}
            >
              등록
            </CommentButton>
          </FlexWidth100>
          <Line />
          {
            // 답변 리스트
            replies?.map((reply: IComment, i: number) => (
              <div key={reply.uuid}>
                <FlexWidth100>
                  <Profile src={reply.writer.profileImageUrl} alt={'profile'} />
                  <CommentTextWrap>
                    {profile?.uuid === reply.writer.uuid && (
                      <MoreIcon
                        onClick={() => {
                          setUpdate(update === '' ? reply.uuid : '');
                        }}
                      />
                    )}
                    <CommentWriter>{reply.writer.displayName}</CommentWriter>
                    <CommentTime>
                      {communintyDateFormat(reply?.createdAt)}
                    </CommentTime>
                    <CommentContent>{reply.content}</CommentContent>
                    {update === reply.uuid && (
                      <>
                        <CommentButton
                          isActive={true}
                          onClick={() => {
                            onSubmitDelete(false, reply.uuid);
                          }}
                        >
                          삭제
                        </CommentButton>
                      </>
                    )}
                  </CommentTextWrap>
                </FlexWidth100>
                <MarginBox
                  onClick={() => {
                    onClickReply(reply.uuid);
                  }}
                >
                  답글쓰기
                </MarginBox>
                {selectReplyUuid === reply.uuid && (
                  <FlexMargin
                    style={{
                      margin: '10px 0px 20px 50px',
                    }}
                  >
                    <CommentInput
                      value={replyCommentStr}
                      onChange={(e) => {
                        console.log(e.target.value);

                        onChangeText(true, e.target.value);
                      }}
                    />
                    <CommentButton
                      isActive={replyCommentStr.length >= 1}
                      onClick={() => {
                        onSubmitComment(true, reply.uuid);
                      }}
                    >
                      등록
                    </CommentButton>
                  </FlexMargin>
                )}
                {
                  // 답변의 답글
                  reply.nestedReplies.length > 0 &&
                    reply.nestedReplies.map((nestReply: NestReplies) => (
                      <FlexMargin
                        style={{
                          marginBottom: 20,
                        }}
                        key={nestReply.uuid}
                      >
                        <Profile
                          src={nestReply.writer.profileImageUrl}
                          alt={'profile'}
                        />
                        <CommentTextWrap>
                          {profile?.uuid === nestReply.writer.uuid && (
                            <MoreIcon
                              onClick={() => {
                                setUpdate(nestReply.uuid);
                              }}
                            />
                          )}
                          <CommentWriter>
                            {nestReply.writer.displayName}
                          </CommentWriter>
                          <CommentTime>
                            {communintyDateFormat(nestReply?.createdAt)}
                          </CommentTime>
                          <CommentContent>{nestReply.content}</CommentContent>
                          {update === nestReply.uuid && (
                            <>
                              <CommentButton
                                isActive={true}
                                onClick={() => {
                                  onSubmitDelete(true, nestReply.uuid);
                                }}
                              >
                                삭제
                              </CommentButton>
                            </>
                          )}
                        </CommentTextWrap>
                      </FlexMargin>
                    ))
                }
              </div>
            ))
          }
        </CommentContainer>
      )}

      {/* 로그인을 하지 않았을때  */}
      {profile?.status !== 'ACTIVE' && (
        <CommentContainer>
          <FlexWidth100>
            <EmptyProfile />
            <CommentInput
              value={commentStr}
              placeholder={'응원의 한마디...'}
              onChange={(e) => {
                console.log(e.target.value);
                onChangeText(false, e.target.value);
              }}
            />
            <CommentButton
              isActive={commentStr.length >= 1}
              onClick={() => {
                if (commentStr.length >= 1) {
                  setModal({
                    id: 'unAuthorized',
                    visible: true,
                  });
                } else {
                  return;
                }
              }}
            >
              등록
            </CommentButton>
          </FlexWidth100>
          <Line />
          {
            // 답변이 있을경우 답변리스트만 출력한다.
            replies?.length > 0 &&
              // 답변 리스트
              replies.map((reply: IComment, i: number) => (
                <div key={reply.uuid}>
                  <FlexWidth100>
                    <Profile
                      src={reply.writer.profileImageUrl}
                      alt={'profile'}
                    />
                    <CommentTextWrap>
                      {profile?.uuid === reply.writer.uuid && <MoreIcon />}
                      <CommentWriter>{reply.writer.displayName}</CommentWriter>
                      <CommentTime>
                        {communintyDateFormat(reply?.createdAt)}
                      </CommentTime>
                      <CommentContent>{reply.content}</CommentContent>
                    </CommentTextWrap>
                  </FlexWidth100>
                  <MarginBox
                    onClick={() => {
                      setModal({
                        id: 'unAuthorized',
                        visible: true,
                      });
                    }}
                  >
                    답글쓰기
                  </MarginBox>
                  {
                    // 답변의 답글
                    reply.nestedReplies.length > 0 &&
                      reply.nestedReplies.map((nestReply: NestReplies) => (
                        <FlexMargin
                          style={{
                            marginBottom: 20,
                          }}
                          key={nestReply.uuid}
                        >
                          <Profile
                            src={nestReply.writer.profileImageUrl}
                            alt={'profile'}
                          />
                          <CommentTextWrap>
                            {profile?.uuid === nestReply.writer.uuid && (
                              <MoreIcon
                                onClick={() => {
                                  setUpdate(nestReply.writer.uuid);
                                }}
                              />
                            )}
                            <CommentWriter>
                              {nestReply.writer.displayName}
                            </CommentWriter>
                            <CommentTime>
                              {communintyDateFormat(nestReply?.createdAt)}
                            </CommentTime>
                            <CommentContent>{nestReply.content}</CommentContent>
                          </CommentTextWrap>
                        </FlexMargin>
                      ))
                  }
                </div>
              ))
          }
        </CommentContainer>
      )}
    </>
  );
};

const CommentContainer = styled.section`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.commonColor.white};
  border: 1px solid ${({ theme }) => theme.commonColor.gray002};
  padding: 1rem 2rem;
  margin-top: 20px;
  @media ${devices.tablet} {
    border: none;
  }
`;

const FlexWidth100 = styled.div`
  display: flex;
  width: 100%;
`;

const FlexMargin = styled.div`
  display: flex;
  margin-left: 50px;
`;

const CommentInput = styled.input`
  background-color: ${({ theme }) => theme.commonColor.gray001};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.commonColor.gray002};
  border-radius: 10px;
  padding: 6px 0 6px 10px;
  width: 100%;
`;

const CommentButton = styled.button<{ isActive: boolean }>`
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.commonColor.pink004 : theme.commonColor.gray001};
  border-radius: 8px;
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.commonColor.pink004 : theme.commonColor.gray002};
  text-align: center;
  width: 200px;
  color: ${({ theme, isActive }) =>
    isActive ? theme.commonColor.white : theme.commonColor.gray004};
  margin-left: 8px;
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'auto')};
  font-family: 'Regular';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: -0.6px;
`;

const Line = styled.div`
  height: 0px;
  border: 1px solid ${({ theme }) => theme.commonColor.gray002};
  margin: 15px 0;
`;

const CommentTextWrap = styled.div`
  background-color: #eee;
  border-radius: 15px;
  width: 100%;
  padding: 10px 16px 12px 16px;
  > div {
    margin: 10px 0;
  }
  position: relative;
`;

const CommentWriter = styled.div`
  color: ${({ theme }) => theme.commonColor.gray005};
  font-size: 14px;
  font-weight: 700;
  font-family: 'Var';
  font-style: normal;
  line-height: normal;
`;

const CommentTime = styled.div`
  color: #aeaeae;
  font-family: 'Var';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.36px;
`;

const CommentContent = styled.div`
  color: ${({ theme }) => theme.commonColor.gray005};
  font-family: 'Var';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px; /* 137.5% */
  letter-spacing: -0.48px;
  resize: none;
  border: none;
  width: 100%;
  outline: none;
`;

const MoreIcon = styled(AiOutlineMore)`
  & > path {
    fill: ${({ theme }) => theme.commonColor.gray005};
  }
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  width: 18px;
  height: 18px;
`;

const MarginBox = styled.span`
  display: inline-block;
  margin: 10px 0 10px 50px;
  color: ${({ theme }) => theme.commonColor.gray005};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.36px;
  font-family: 'Var';
  cursor: pointer;
`;

const EmptyProfile = styled.div`
  background-color: #eee;
  border-radius: 50%;
  margin-right: 10px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
`;

const Profile = styled.img`
  border-radius: 50%;
  margin-right: 10px;
  object-fit: cover;
  overflow: hidden;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
`;

export default Comment;
